// @flow
import React, { Component, Fragment } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import Cleave from 'cleave.js/dist/cleave-react';
import { Element } from 'react-scroll/modules';
import Loading from '../../../../../elements/Loading/Loading';
import Modal from '../../../../../elements/Modal/Modal';
import Select from '../../../../../elements/Select/Select';
import RadioButton from '../../../../../elements/RadioButton/RadioButton';
import { FormValidator } from '../../../../../utils/form';
import InputQuantity from '../../../../../elements/InputQuantity/InputQuantity';
import InputPrice from '../../../../../elements/InputPrice/InputPrice';
import DetailOption from '../DetailOption/DetailOption';
import LowestPrice, { LowestPriceEmpty } from './LowestPrice/LowestPrice';
import VolumetricOptions from './VolumetricOptions/VolumetricOptions';
import { getLocalizedCountry } from '../../../../local-values/utils/countries';
import { freeShippingCap } from '../../../../local-values/utils/benefits';
import InputQuantityRound from '../../../../../elements/InputQuantityRound/InputQuantityRound';
import InputWeight from '../../../../../elements/InputWeight/InputWeight';

import style from './ProductForm.scss';
import './ProductForm.css';
import boxStyle from '../../../../../assets/styles/box.scss';
import formStyle from '../../../../../assets/styles/form.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import icons from '../../../../../assets/styles/icons.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';

import withBoxImg from '../../../../../assets/img/new-order/with-box.png';
import withoutBoxImg from '../../../../../assets/img/new-order/without-box.png';

import type { Option } from '../../../../../elements/Select/Select';
import type { Category, OnlineStore, ScrappedProduct, WishList } from '../../../../../entities';
import type { InputQuantityAction } from '../../../../../elements/InputQuantity/InputQuantity';
import type { Forecast } from '../../../QuoteStep/api';
import type { LocaleState } from '../../../../locale/duck';
import type { LocalValuesState } from '../../../../local-values/duck';

type Props = {
  +handleChangeInputText: (e: Event, value?: string) => void;
  +handleChangeInputUrl: (e: Event) => void;
  +handleFocusInputText: (e: Event) => void;
  +handleBlurCleave: (e: Event) => void;
  // +handleClickRefresh: (e: Event) => void;
  +handleDeleteImage: () => void;
  +handleChangeImage: (acceptedFiles: File[]) => void;
  +handleShippingInit: (cleave: Cleave) => void;
  +handlePriceInit: (cleave: Cleave) => void;
  +handleWeightInit: (cleave: Cleave) => void;
  +handleChangeCleave: (e: Event) => void;
  +handleChangeCategory: (option: Option) => void;
  +handleChangeAmount: (action: InputQuantityAction) => void;
  +handleChangeWeightUnit: (index: string) => void;
  +handleChangeVolume: (value: number) => void;
  +localValues: LocalValuesState;
  +handleChangeBoxOption: (index: number) => void;
  +handleFocusWishList: () => void;
  +handleClickAddCart: () => void;
  +handleClickBuyNow: () => void;
  +handleOpenShippingHelpModal: () => void;
  +handleSelectWishList: (option: Option) => void;
  +handleNativeSelectWishList: (e: Event) => void;
  +handleDeleteProduct: () => void;
  +handleOnKeyPress: (e: KeyboardEvent) => void;
  +categories?: Category[];
  +localeState: LocaleState;
  +departureCountry: {
    value: string;
    errors?: string[];
  };
  +url: {
    value: string;
    errors?: string[];
  };
  +image: {
    value?: string;
    valueURI?: string;
    errors?: string[];
  };
  +name: {
    value: string;
    errors?: string[];
  };
  +description: {
    value: string;
    errors?: string[];
  };
  +shipping: {
    value: string;
    errors?: string[];
  };
  +onlineStore: OnlineStore;
  +price: {
    value: string;
    errors?: string[];
  };
  +categoryId: {
    value: string;
    errors?: string[];
  };
  +amount: {
    value: number;
    errors?: string[];
  };
  +weightUnit: {
    value: string;
  };
  +weight: {
    value: string;
    errors?: string[];
  };
  +volumeTier: {
    value: ?number;
    code: string;
    errors?: string[];
  };
  +withBox: {
    value: number;
    errors?: string[];
  };
  +isScrapping: boolean;
  +loadingForecast: boolean;
  +forecast?: Forecast;
  +wishLists?: WishList[];
  +hideOptions: boolean;
  // +chosenVariants?: { [key: string]: number };
  +scrappedProduct: ScrappedProduct;
  +handleChangeDetailOption: (dimension: string, value: string) => void;
  +detailOptions: {
    [key: string]: {
      value: string;
      errors?: string[];
      disabled?: {
        [key: string]: boolean;
      };
    };
  };
  +preferredImportationMethod: string;
};

type State = {
  detailCount: number;
  revisionHintModalOpen: boolean;
};

const messages = defineMessages({
  wishList: {
    id: 'newOrder.product.form.wishList.placeholder',
  },
  help: {
    id: 'newOrder.product.form.url.help',
  },
  free: {
    id: 'newOrder.freeShippingBanner.free',
  },
  generalOne: {
    id: 'newOrder.freeShippingBanner.general.one',
  },
  generalTwo: {
    id: 'newOrder.freeShippingBanner.general.two',
  },
});

const withBoxOptions = [
  {
    intl: 'newOrder.product.form.box.no',
    img: withoutBoxImg,
    alt: 'withoutBox',
  },
  {
    intl: 'newOrder.product.form.box.yes',
    img: withBoxImg,
    alt: 'withBox',
  },
];

const maxCharactersDetailCount = 100;

export default class ProductForm extends Component<Props, State> {
  state = {
    detailCount: 100,
    revisionHintModalOpen: false,
  };
  addToCartId: string = 'addToCartButton';

  handleClickDeleteImage(e: Event) {
    const { handleDeleteImage } = this.props;
    e.preventDefault();
    e.stopPropagation();
    handleDeleteImage();
  }

  handleChangeTextarea = (e: Event) => {
    const { handleChangeInputText } = this.props;
    if (e.target instanceof HTMLTextAreaElement) {
      const { value } = e.target;
      const text = value.length ? value.slice(0, maxCharactersDetailCount) : '';
      this.setState({ detailCount: maxCharactersDetailCount - text.length });
      handleChangeInputText(e, text);
    }
  };

  buildMessage = () => {
    const { localValues, localeState } = this.props;
    const country = localeState && getLocalizedCountry(localeState.domainCountry || 'PE',
      this.context.intl.formatMessage, localValues.countriesMap);
    const free = <strong>{this.context.intl.formatMessage(messages.free)}</strong>;
    return <p>{free} {`${this.context.intl.formatMessage(messages.generalOne)} ${country} `}
      {` ${this.context.intl.formatMessage(messages.generalTwo)} $${freeShippingCap}`}</p>;
  };

  render() {
    const {
      handleChangeInputText, handleChangeInputUrl, handleFocusInputText, handleChangeImage,
      handlePriceInit, handleWeightInit, handleChangeCleave, handleChangeCategory, handleChangeAmount,
      handleChangeWeightUnit, handleFocusWishList, handleClickAddCart, handleClickBuyNow, handleShippingInit,
      onlineStore, handleBlurCleave, handleDeleteProduct, handleChangeBoxOption, handleOnKeyPress,
      categories, url, image, name, description, price, categoryId, amount, weightUnit, weight, shipping,
      withBox, isScrapping, loadingForecast, forecast, wishLists, handleNativeSelectWishList, hideOptions,
      localeState, handleOpenShippingHelpModal, volumeTier, handleChangeVolume, localValues,
      scrappedProduct, handleChangeDetailOption, detailOptions, preferredImportationMethod,
    } = this.props;
    const { detailCount, revisionHintModalOpen } = this.state;
    
    const categoryOptions: Option[] = (categories || [])
      .filter((category: Category) => category.enabled)
      .map((category: Category) => {
        return ({
          value: category.id,
          label: category.translations[localeState.locale.toUpperCase()].name,
        })
      });

    if (url.errors) {
      console.log('url.errors', url.errors);
    }

    const urlError = url.errors ? FormValidator.buildErrorLabel(url.errors, {
      required: 'newOrder.product.form.url.error.required',
      urlFormat: 'newOrder.product.form.url.error.format',
      notCartUrlFormat: 'newOrder.product.form.url.error.notCartUrlFormat',
      amazonSpain: 'newOrder.product.form.url.error.amazonSpain',
      ebayAuction: 'newOrder.product.form.url.error.ebayAuction',
      unscrapped: 'newOrder.product.form.url.error.unscrapped',
      // invalidDomain: 'newOrder.product.form.url.error.invalidDomain',
      // urlNotStore: 'newOrder.product.form.url.error.urlNotStore',
      // storeBlacklist: 'newOrder.product.form.url.error.storeBlacklist',
    }) : undefined;

    const nameError = !!name.errors && FormValidator.buildErrorLabel(name.errors, {
      required: 'newOrder.product.form.name.error.required',
      unscrapped: 'newOrder.product.form.name.error.unscrapped',
    });

    const imageError = !!image.errors && FormValidator.buildErrorLabel(image.errors, {
      required: 'newOrder.product.form.uploadImage.error.required',
      unscrapped: 'newOrder.product.form.uploadImage.error.unscrapped',
    });

    const priceError = !!price.errors && FormValidator.buildErrorLabel(price.errors, {
      required: 'newOrder.product.form.price.error.required',
      unscrapped: 'newOrder.product.form.price.error.unscrapped',
    });

    const categoryError = !!categoryId.errors && FormValidator.buildErrorLabel(categoryId.errors, {
      required: 'newOrder.product.form.category.error.required',
      unscrapped: 'newOrder.product.form.category.error.unscrapped',
    });

    const weightError = !!weight.errors && FormValidator.buildErrorLabel(weight.errors, {
      required: 'newOrder.product.form.weight.error.required',
      greaterThanZero: 'newOrder.product.form.weight.error.greaterThanZero',
      lessThanFiveKg: 'newOrder.product.form.weight.error.lessThanFiveKg',
      unscrapped: 'newOrder.product.form.weight.error.unscrapped',
    });

    const withBoxError = !!withBox.errors && FormValidator.buildErrorLabel(withBox.errors, {
      required: 'newOrder.product.form.box.error.required',
    });

    /*const lowerPrice = forecast && forecast.length && forecast[forecast.length - 1].total || '- - -';
    const forecastStyle = (!loadingForecast && !!forecast && forecast.length > 0)
      ? style.hasForecast : style.noForecast;*/

    const wishListOptions: Option[] = (wishLists || []).map((wishList: any) => ({
      value: wishList.name,
      labelId: wishList.intl,
      label: wishList.name,
    }));

    const wishListStaticOptions: Option[] = [{
      value: 'new',
      labelId: 'newOrder.product.form.wishList.new',
      icon: icons.iconPlusMedium,
    }];

    const nativeOptions = [...wishListOptions, ...wishListStaticOptions].map((option: Option, n: number) =>
      <option key={n}
              value={option.value}>
        {option.labelId ? this.context.intl.formatMessage({ id: option.labelId }) : option.label}
      </option>);

    const dropzone = (
      <article className={style.dropzoneWrap}>
        {(image.value || image.valueURI) &&
        <button key="newOrderImageDelete"
                type="button"
                className={style.dropzoneDeleteImage}
                onClick={this.handleClickDeleteImage.bind(this)}>
          <i className={icons.iconCloseLight}/>
        </button>}
        <Element name="image">
          <Dropzone key="dropzone"
                    accept="image/jpeg,image/jpg,image/png"
                    multiple={false}
                    className={classNames(style.dropzone, {
                      [style.error]: image && image.errors && image.errors[0] !== 'unscrapped',
                      [style.yellow]: image && image.errors && image.errors[0] === 'unscrapped',
                    })}
                    activeClassName={style.active}
                    disabledClassName={style.disabled}
                    maxSize={2097152}
                    onDrop={handleChangeImage}>
            {/* item.previewImage &&
        <img className={style.preview} src={item.previewImage} alt={item.name ? item.name.value : ''}/>*/}

            {(image && (image.value || image.valueURI)) &&
            <img src={image.value || image.valueURI}
                 alt={name.value || ''}/>
            }
            {(!image || (!image.value && !image.valueURI)) &&
            <section className={style.dropzoneContent}>
              <i className={icons.symbolPlus}/>
              <FormattedMessage id="newOrder.product.form.uploadImage.text">{
                txt => <p className={style.dropzoneTitle}>{txt}</p>
              }</FormattedMessage>
              {/*<FormattedMessage id="newOrder.product.form.uploadImage.button">{
                txt => <button type="button"
                               className={classNames(buttonStyle.btn, buttonStyle.greyInverse)}>{txt}</button>
              }</FormattedMessage>*/}
            </section>}
            {/* item.isUploadingImage && <div className={classNames(progressBarStyle.progressBar, style.progressBar)}>
          <div className={progressBarStyle.indeterminate}/>
        </div>*/}
          </Dropzone>
          {imageError}
        </Element>
      </article>
    );

    const volumetricCat = localValues && localValues.categoriesMap;

    const generateWhatsappLink = (whatsappNumber: string, text: string) => 
      `https://wa.me/${whatsappNumber}?text=${text}`;
    
    return (<Fragment>
      <main className={style.productFormWrap}>
        <section className={style.productTopWrap}>
          <FormattedMessage id="newOrder.product.form.title">{
            txt => (<h2 className={style.title}>{txt}</h2>)
          }</FormattedMessage>
          <FormattedMessage id="newOrder.product.form.hint">{
            txt => <p className={style.hint}>{txt}</p>
          }</FormattedMessage>
          <section className={formStyle.formWrap}>
            <section className={classNames(formStyle.row, formStyle.inputRow, style.urlWrap, formStyle.ABExperiment)}>
              <FormattedMessage id="newOrder.product.form.url.label">{
                txt => <h3 className={style.urlTitle}>{txt}</h3>
              }</FormattedMessage>
              <section className={style.inputWrap}>
                <FormattedMessage id="newOrder.product.form.url.placeholder">{
                  txt => <input type="text"
                                name="url"
                                autoComplete="disabled"
                                value={url.value}
                                className={classNames(inputStyle.input, style.urlInput,
                                  { [inputStyle.red]: !!url.errors },
                                  { [inputStyle.yellow]: !!url.errors && url.errors[0] === 'unscrapped' },
                                  { [inputStyle.lightBlue]: !!url.value })}
                                onChange={handleChangeInputUrl}
                                onFocus={handleFocusInputText}
                                placeholder={txt}
                                onKeyPress={handleOnKeyPress}/>
                }</FormattedMessage>
                {isScrapping && <Loading type="sk-three-bounce"
                                         color="lightBlue"
                                         number={3}
                                         className={style.loading}/>}
                {urlError}
              </section>
            </section>
          </section>
        </section>
        <section className={style.productImageAndQuantityWrap}>
          {/*Image*/}
          {dropzone}
          {/*Quantity*/}
          <article className={style.quantity}>
            <FormattedMessage id="newOrder.product.form.quantity.label">{
              txt => (<label className={formStyle.label}>{txt}</label>)
            }</FormattedMessage>
            <InputQuantityRound disableLess={amount.value === 1}
                                disableMore={amount.value === 5}
                                quantity={amount ? `${amount.value}` : `1`}
                                handleChange={handleChangeAmount}/>
          </article>
        </section>
        <LowestPriceEmpty loadingForecas={loadingForecast}
                          forecas={forecast}/>
        <section className={style.productMainWrap}>
          <form className={classNames(formStyle.formWrap)}
                onSubmit={(e: Event) => e.preventDefault()}
                autoComplete="off">
            {/*Name*/}
            <section className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id="newOrder.product.form.name.label">{
                txt => (<label className={formStyle.label}>{txt}:</label>)
              }</FormattedMessage>
              <FormattedMessage id="newOrder.product.form.name.placeholder">{
                txt => <input type="text"
                              name="name"
                              id="name"
                              autoComplete="disabled"
                              value={name.value}
                              className={classNames(inputStyle.input, {
                                [inputStyle.red]: !!name.errors,
                                [inputStyle.yellow]: !!name.errors && name.errors[0] === 'unscrapped',
                                [inputStyle.lightBlue]: !!name.value,
                              })}
                              onChange={handleChangeInputText}
                              onFocus={handleFocusInputText}
                              placeholder={txt}/>
              }</FormattedMessage>
              {nameError}
            </section>
            {/*Detail Options*/}
            {!!scrappedProduct && !!scrappedProduct.detailOptions &&
            Object.keys(scrappedProduct.detailOptions.variationDimensions).map((key: string, idx: number) =>
              <DetailOption key={idx}
                            dimension={key}
                            handleChange={handleChangeDetailOption}
                            selectedOption={detailOptions[key]}
                            options={scrappedProduct.detailOptions.variationDimensions[key]}/>)}
            {/*Description*/}
            {(!scrappedProduct || (!!scrappedProduct && !scrappedProduct.detailOptions)) &&
            <section className={classNames(formStyle.row, formStyle.inputRow, style.description)}>
              <FormattedMessage id="newOrder.product.form.description.label">{
                txt => (<label className={formStyle.label}>{txt}:
                  <span className={formStyle.optional}>
                    {this.context.intl.formatMessage({ id: 'newOrder.product.form.url.optional.label' })}
                  </span>
                </label>)
              }</FormattedMessage>
              <FormattedMessage id="newOrder.product.form.description.count">{
                txt => (<label className={classNames(formStyle.label, style.counter)}>{detailCount}
                  {detailCount === 1 ? ` ${txt}` : ` ${txt.replace('Ã¡', 'a')}es`}</label>)
              }</FormattedMessage>
              <FormattedMessage id="newOrder.product.form.description.placeholder">{
                txt => <textarea name="description"
                                 autoComplete="disabled"
                                 value={description.value}
                                 className={classNames(inputStyle.input, {
                                   [inputStyle.lightBlue]: description && description.value,
                                 })}
                                 onChange={this.handleChangeTextarea}
                                 onFocus={handleFocusInputText}
                                 placeholder={txt}/>
              }</FormattedMessage>
            </section>}

            <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap,
              formStyle.responsiveMedium)}>
              {/*Price*/}
              <article className={formStyle.col}>
                <FormattedMessage id="newOrder.product.form.price.label">{
                  txt => (<label className={formStyle.label}>
                    {txt}:
                  </label>)
                }</FormattedMessage>
                <InputPrice name="price"
                            value={price.value}
                            hasError={!!price.errors}
                            errors={price.errors}
                            handleFocusInputText={handleFocusInputText}
                            handleChangeCleave={handleChangeCleave}
                            handleBlurCleave={handleBlurCleave}
                            handleInit={handlePriceInit}
                />
                {priceError}
              </article>
              {/*Category*/}
              <article className={classNames(formStyle.col)}>
                <FormattedMessage id="newOrder.product.form.category.label">{
                  txt => (<label className={formStyle.label}>{txt}:</label>)
                }</FormattedMessage>
                <FormattedMessage id="newOrder.product.form.category.placeholder">{
                  txt => <Select options={categoryOptions}
                                 id="categoryId"
                                 className={classNames({
                                   'light-blue': categoryId && categoryId.value,
                                   'red': categoryId && categoryId.errors && categoryId.errors[0] !== 'unscrapped',
                                   'yellow': categoryId && categoryId.errors && categoryId.errors[0] === 'unscrapped',
                                 })}
                                 value={categoryId.value}
                                 hasError={!!categoryId.errors}
                                 placeholder={txt}
                                 handleChange={handleChangeCategory}/>
                }</FormattedMessage>
                {categoryError}
              </article>
              {/*Shipping*/}
              {/*<article className={formStyle.col}>
                <FormattedMessage id="newOrder.product.form.shipping.label">{
                  txt => (<label className={formStyle.label}>{txt}:
                    <span className={style.help}
                          onClick={handleOpenShippingHelpModal}>
                    {this.context.intl.formatMessage(messages.help)}
                  </span>
                  </label>)
                }</FormattedMessage>
                {shipping && handleChangeCleave && handleShippingInit &&
                <InputPrice name="shipping"
                            value={shipping.value}
                            hasError={!!shipping.errors}
                            errors={shipping.errors}
                            handleFocusInputText={handleFocusInputText}
                            handleChangeCleave={handleChangeCleave}
                            handleBlurCleave={handleBlurCleave}
                            handleInit={handleShippingInit}
                />}
              </article>*/}
            </section>
            {categoryId.value === '9' && <FormattedMessage id="newOrder.product.form.revision.hint">{
              txt => <span className={style.revisionHint}>
                <i className={icons.symbolWarning}/>{txt} <button onClick={() => this.setState({revisionHintModalOpen: true})}><i className={icons.symbolQuestionCircle} /></button>
              </span>
            }</FormattedMessage>}
            <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap, formStyle.rowNoMargin,
              formStyle.responsiveMedium)}>
              {/* Weight */}
              {!!localValues.categoriesMap && !!categoryId.value &&
              localValues.categoriesMap[`${categoryId.value}`].criteria === 'WEIGHT' &&
              <article className={classNames(formStyle.col)}>
                <FormattedMessage id="newOrder.product.form.weight.label">{
                  txt => (<label className={formStyle.label}>{txt}:</label>)
                }</FormattedMessage>
                <InputWeight name="weight"
                             value={weight.value}
                             hasError={!!weight.errors}
                             errors={weight.errors}
                             weightUnit={weightUnit}
                             handleBlurCleave={handleBlurCleave}
                             handleFocusInputText={handleFocusInputText}
                             handleChangeCleave={handleChangeCleave}
                             handleChangeWeightUnit={handleChangeWeightUnit}
                             handleInit={handleWeightInit}
                />
                {weightError}
              </article>}
            </section>
            {!!volumetricCat && !!volumetricCat[categoryId.value] &&
            volumetricCat[categoryId.value].volumeTiers.length !== 0 &&
            <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.rowNoMargin)}>
              <Element name="volumeTier">
                <VolumetricOptions value={volumeTier.value}
                                   onChange={handleChangeVolume}
                                   localeState={localeState}
                                   errors={volumeTier.errors}
                                   category={volumetricCat[categoryId.value]}/>
              </Element>
            </section>}

            {/* With box */}
            {!!localValues.categoriesMap && !!categoryId.value &&
            localValues.categoriesMap[`${categoryId.value}`].originalPackagingSelectable &&
            <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.rowNoMargin)}>
              {/*<FormattedMessage id="newOrder.product.box.label">{
              txt => (<p className={classNames(boxStyle.subtitle, style.smallSpace)}>{txt}</p>)
            }</FormattedMessage>*/}
              <FormattedMessage id="newOrder.product.form.box.label">{
                txt => (<label className={classNames(formStyle.label, style.withBoxLabel)}>{txt}</label>)
              }</FormattedMessage>
              <Element name="withBox">
                <section className={style.withBox}>
                  {withBoxOptions.map((option: { [key: string]: string }, index: number) =>
                    (<article key={index}
                              className={classNames(style.packaging, { [style.selected]: withBox.value === index })}
                              onClick={() => handleChangeBoxOption(index)}>
                      {/*<section className={style.circle}>
                      <div className={classNames(style.point, { [style.selected]: withBox.value === index })}/>
                    </section>*/}
                      <section className={style.wrapper}>
                        <RadioButton name="withBox"
                                     value={index}
                                     selected={withBox.value}
                                     handleChange={_ => {
                                     }}/>
                        <img src={option.img} alt={option.alt}/>
                        <FormattedMessage id={option.intl}>{
                          txt => <p>{txt}</p>
                        }</FormattedMessage>
                      </section>
                    </article>),
                  )}
                  {withBoxError}
                </section>
              </Element>
            </section>}
          </form>
          <FormattedMessage id="newOrder.product.form.detail.hint">{
            txt => <span className={style.hint}><i className={icons.symbolWarning}/>{txt}</span>
          }</FormattedMessage>
        </section>
        <section className={classNames(style.sellerWrap, { [style.noBorder]: !(onlineStore && onlineStore.logo) })}>
          {onlineStore && onlineStore.logo && <section className={style.seller}>
            <FormattedMessage id="newOrder.product.form.seller">{
              txt => <p>{txt}</p>
            }</FormattedMessage>
            <div className={style.sellerImgWrap}>
              <img src={onlineStore.logo}
                   alt={onlineStore.name}
                   className={style.sellerImg}/>
            </div>
          </section>}
          <section className={style.productDeleteWrap}>
            <FormattedMessage id="newOrder.products.form.button.delete">{
              txt => <button onClick={handleDeleteProduct}
                             className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.deleteButton)}>
                <i className={icons.iconTrashcanLight}/>{txt}</button>
            }</FormattedMessage>
          </section>
        </section>
        <section className={style.bottomWrap}>
          {/*<FormattedMessage id="newOrder.products.form.button.buyNow">{
            txt => <button className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                           onClick={handleClickBuyNow}>{txt}</button>
          }</FormattedMessage>*/}

          <article className={classNames(style.buttonWrap)}>
            <FormattedMessage id="newOrder.products.form.button.quoteNow">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                             onClick={handleClickBuyNow}>{txt}</button>
            }</FormattedMessage>


            {!(preferredImportationMethod === 'full' || preferredImportationMethod === 'lite') &&
            <FormattedMessage id="newOrder.products.form.button.addCart">{
              txt => <button id={this.addToCartId}
                             className={classNames(buttonStyle.btn, buttonStyle.orange)}
                             onClick={handleClickAddCart}>
                <i className={icons.iconShoppingCartMedium}/>
                {txt}</button>
            }</FormattedMessage>}
          </article>
          {/*Wishlist dropdown*/}
          {/*<Default>
            <Select options={wishListOptions}
                    className={classNames(style.wishLists, 'wishLists', hideOptions ? 'hide' : '')}
                    value=''
                    placeholder={this.context.intl.formatMessage(messages.wishList)}
                    handleChange={handleSelectWishList}
                    handleFocus={handleFocusWishList}
                    staticOptions={wishListStaticOptions}
                    staticOptionDirection='bottom'
                    hideOptions={hideOptions}/>
          </Default>
          <Mobile>
            <section className={style.wishListOptions}>
              <FormattedMessage id={messages.wishList.id}>{
                txt => <label>{txt}</label>
              }</FormattedMessage>
              <select onChange={handleNativeSelectWishList}
                      onFocus={handleFocusWishList}>
                {nativeOptions}
              </select>
              <i className={classNames(style.down, icons.iconChevronDownMedium)}/>
              <i className={classNames(style.up, icons.iconChevronUpMedium)}/>
            </section>
          </Mobile>*/}
          {/*<section className={style.hint}>
            {chosenVariant.index === 0 && <Fragment>
              <i className={icons.iconSecureCheckMedium}/>
              <FormattedMessage id="newOrder.product.hint.right">{
                text => <p>{text}</p>
              }</FormattedMessage>
            </Fragment>}
            <i className={icons.symbolTruck}/>
            {this.buildMessage()}
          </section>*/}
        </section>
      </main>
      <Modal open={revisionHintModalOpen}
             handleClose={() => this.setState({revisionHintModalOpen: false})}
             closeOnEsc>
        <h2 className={style.modalTitle}>Revisión de pedido</h2>

        <p className={style.modalText}>
          Una vez realices tu pedido, un miembro de nuestro
          equipo revisará que la cotización esté correcta.
          <br/>
          <br/>
          Algunos productos especiales tienen cotizaciones
          específicas.
          <br/>
          <br/>
          Contáctanos para obtener una cotización exacta.
        </p>
        
        <br/>
        
        <a href={generateWhatsappLink('5117064549', 'Hola Qempo! Quiero cotizar un producto de la categoría OTROS')}
           target="_blank"   
           className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.modalButton)}>
          Contactar por WhatsApp
        </a>
      </Modal>
    </Fragment>);
  }
}

ProductForm.propTypes = {
  handleChangeInputText: PropTypes.func.isRequired,
  handleChangeInputUrl: PropTypes.func.isRequired,
  handleFocusInputText: PropTypes.func.isRequired,
  handleBlurCleave: PropTypes.func.isRequired,
  // handleClickRefresh: PropTypes.func.isRequired,
  handleDeleteImage: PropTypes.func.isRequired,
  handleChangeImage: PropTypes.func.isRequired,
  handleShippingInit: PropTypes.func.isRequired,
  handlePriceInit: PropTypes.func.isRequired,
  handleChangeCleave: PropTypes.func.isRequired,
  handleChangeCategory: PropTypes.func.isRequired,
  handleChangeAmount: PropTypes.func.isRequired,
  handleChangeWeightUnit: PropTypes.func.isRequired,
  handleChangeVolume: PropTypes.func.isRequired,
  handleChangeBoxOption: PropTypes.func.isRequired,
  handleFocusWishList: PropTypes.func.isRequired,
  handleClickAddCart: PropTypes.func.isRequired,
  handleClickBuyNow: PropTypes.func.isRequired,
  handleOpenShippingHelpModal: PropTypes.func.isRequired,
  handleSelectWishList: PropTypes.func.isRequired,
  handleNativeSelectWishList: PropTypes.func.isRequired,
  handleDeleteProduct: PropTypes.func.isRequired,
  handleOnKeyPress: PropTypes.func.isRequired,
  categories: PropTypes.array,
  localeState: PropTypes.object.isRequired,
  localValues: PropTypes.object,
  departureCountry: PropTypes.object.isRequired,
  url: PropTypes.object.isRequired,
  image: PropTypes.object.isRequired,
  name: PropTypes.object.isRequired,
  description: PropTypes.object.isRequired,
  shipping: PropTypes.object.isRequired,
  onlineStore: PropTypes.object.isRequired,
  price: PropTypes.object.isRequired,
  categoryId: PropTypes.object.isRequired,
  amount: PropTypes.object.isRequired,
  weightUnit: PropTypes.object.isRequired,
  weight: PropTypes.object.isRequired,
  volumeTier: PropTypes.object.isRequired,
  withBox: PropTypes.object.isRequired,
  isScrapping: PropTypes.bool.isRequired,
  loadingForecast: PropTypes.bool.isRequired,
  forecast: PropTypes.array,
  wishLists: PropTypes.array,
  hideOptions: PropTypes.bool.isRequired,
  // chosenVariants: PropTypes.object,
  scrappedProduct: PropTypes.object,
  handleChangeDetailOption: PropTypes.func.isRequired,
  detailOptions: PropTypes.object,
};

ProductForm.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/ProductStep/components/ProductForm/ProductForm.jsx